import * as Sentry from '@sentry/browser';
import { noop } from 'lodash';
import { Auth } from 'aws-amplify';
import React, { useState, useEffect } from 'react';
import constate from 'constate';
import siteConfig from '~/config';

import getDisplayName from 'Services/getDisplayName';

const useApp = ({ analyticsContext }) => {
  const [isAuthenticated, userHasAuthenticated] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const [identityEmail, setIdentityEmail] = useState(null);

  const onLoad = async () => {
    await Auth.currentSession()
      .then((data) => {
        const identity = data.idToken.payload.email || data.idToken.payload.phone_number;
        const { sendSessionProperties, identifyUser } = analyticsContext;
        setIdentityEmail(identity);
        Sentry.configureScope((scope) => {
          scope.setTag('user_type', 'practitioner');
          scope.setUser({ email: identity });
        });
        identifyUser(identity);
        sendSessionProperties({
          user_type: 'practitioner',
        });
        userHasAuthenticated(true);
      })
      .catch(() => {
        userHasAuthenticated(false);
      });

    setIsAuthenticating(false);
  };

  useEffect(() => {
    onLoad();
  }, []);

  const forgot = () => {
    window.location.href = siteConfig.FORGOT_PASSWORD_URL;
  };

  const logout = async (callback = noop) => {
    setIsAuthenticating(true);
    await Auth.signOut()
      .then(() => {
        userHasAuthenticated(false);
        setIsAuthenticating(false);
        callback();
      });
  };

  const login = async (email, password, successCallback = noop, failureCallback = noop) => {
    setIsAuthenticating(true);
    await Auth.signIn(email, password)
      .then(() => {
        const { sendSessionProperties, identifyUser } = analyticsContext;
        setIdentityEmail(email);
        Sentry.configureScope((scope) => {
          scope.setTag('user_type', 'practitioner');
          scope.setUser({ email });
        });
        identifyUser(email);
        sendSessionProperties({
          user_type: 'practitioner',
        });
        userHasAuthenticated(true);
        setIsAuthenticating(false);
        successCallback();
      })
      .catch((e) => {
        failureCallback(e.message);
      });
  };

  return {
    forgot,
    login,
    logout,
    identityEmail,
    isLoggedIn: isAuthenticated,
    isAuthenticating,
  };
};

export const [AppProvider, useAppContext] = constate(useApp);

const defaultHOCPropConfig = [
  'forgot',
  'login',
  'login',
  'identityEmail',
  'isLoggedIn',
  'isAuthenticating',
];

export const withAppContextHOC = (config = defaultHOCPropConfig) => (WrappedComponent) => {
  const withApp = (props) => {
    const appContext = useAppContext();
    return (
      <WrappedComponent
        {...config.reduce((obj, item) => ({
          ...obj,
          [item]: appContext[item],
        }), {})}
        {...props}
      />
    );
  };
  withApp.displayName = `withAppContextHOC(${getDisplayName(WrappedComponent)})`;
  return withApp;
};

export default useAppContext;
